import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useDispatch, useSelector } from "react-redux";
import {
  availableDates,
  availableDatesLoading,
  availableTimes,
  bookingTaxpayerID,
  createBookingFormState,
  getAllAvailableTimes,
  selectedTime,
  setSelectedDate,
  setSelectedTime,
  setTimesDataLoading,
  timesDataLoading,
} from "../../features/BookingsSlice";
import { useCallback, useEffect, useState } from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AvailableDaysResponse, SlotsRequest } from "../../models/Slots";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AvailabilityStatus } from "../../models/Enums";

const Calendar = () => {
  const dispatch = useDispatch();
  const dates = useSelector(availableDates);
  const times = useSelector(availableTimes);
  const datesLoading = useSelector(availableDatesLoading);
  const selectedBookingTime = useSelector(selectedTime);
  const taxPayer = useSelector(bookingTaxpayerID);
  const bookingDetailsForm = useSelector(createBookingFormState);
  const isTimesDataLoading = useSelector(timesDataLoading);

  const [calendarDate, setCalendarDate] = useState<Date | null>(null);
  const [bookingsAtMax, setBookingsAtMax] = useState(false);

  const buildSlotsRequestObject = useCallback((data: Date): SlotsRequest => {
    return {
      date: data.toISOString(),
      bookingTaxpayerID: taxPayer,
      branchCode: bookingDetailsForm?.values?.branch ?? null,
      reasonPermutationId: bookingDetailsForm?.values?.reason
    }
  }, [taxPayer, bookingDetailsForm?.values?.branch, bookingDetailsForm?.values?.reason]);

  useEffect(() => {
    if(dates && dates.length > 0 && !datesLoading) {
      const newCalendarDate = new Date(dates[0].Date);
      setCalendarDate(newCalendarDate);
      const data = buildSlotsRequestObject(newCalendarDate);
      dispatch(setTimesDataLoading(true));
      dispatch(getAllAvailableTimes(data));
      dispatch(setSelectedDate(newCalendarDate.toISOString()));
      setBookingsAtMax(false);

      const month = new Date().getMonth() + 1;  
      const months = dates.map((date: AvailableDaysResponse) => new Date(date.Date).getMonth() + 1);
  
      if(!months.includes(month))
      {
        const newDate = new Date(dates[0].Date);        
        setCalendarDate(newDate);
        const data = buildSlotsRequestObject(newDate);
        dispatch(setSelectedDate(newDate.toISOString()));
        dispatch(setTimesDataLoading(true));
        dispatch(getAllAvailableTimes(data));
      }

      dates.forEach((date: AvailableDaysResponse) => {
        if(date.AvailabilityStatus && date.AvailabilityStatus === AvailabilityStatus.TaxPractitionerMaxBookings) {
          const iosDateString = new Date(date.Date).toDateString();
          const timestamp = new Date(iosDateString).getTime();
          const button = document.querySelector(`[data-timestamp="${timestamp}"]`);
          if(button) {
            button.classList.add('max-bookings-reached');
          }
        }
      });
    } else {
      setCalendarDate(new Date());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates, datesLoading, dispatch, buildSlotsRequestObject]);

  const checkDateAvailability = (date: Date): boolean => {
    const found = dates.find((day: AvailableDaysResponse) => new Date(day.Date).toDateString() === new Date(date.toISOString()).toDateString());
    if (found) {
      return false;
    }
    return true;
  };

  const dateChanged = (value: Date | null): void => {
    if (value) {
      setCalendarDate(value);
      const stringDate = new Date(value).toDateString();
      const timestamp = new Date(stringDate).getTime();
      const button = document.querySelector(`[data-timestamp="${timestamp}"]`);
      if(button && button.classList.contains('max-bookings-reached')) {
        setBookingsAtMax(true);
      } else {
        const data = buildSlotsRequestObject(value);
        dispatch(setTimesDataLoading(true));
        dispatch(getAllAvailableTimes(data));
        dispatch(setSelectedDate(value.toISOString()));
        setBookingsAtMax(false);
      }
    }
  };

  const timeSelected = (time: string): void => {
    dispatch(setSelectedTime(time));
  };

  return dates && dates.length > 0 
    ? (<Box sx={(theme) => ({ 
        display: "flex", justifyContent: "space-between", 
        flexDirection: "row",
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
        }
      })}>
        <Box
          sx={{
            border: "1px solid #EDEDED",
            borderRadius: "8px",
            display: "flex",
            flex: "1 1 50%",
            mr: "4px",
            p: "8px",
            flexDirection: "column",
            mt: "8px"
          }}
        >
          <Typography sx={(theme) => ({ fontSize: theme.typography.h3 })}>Select Date</Typography>
          <Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticDatePicker
                slotProps={{
                  actionBar: { actions: [] },
                  toolbar: { hidden: true },
                }}
                className="custom-static-date-picker"
                sx={{ width: "100%" }}
                disablePast={true}
                shouldDisableDate={checkDateAvailability}
                onChange={dateChanged}
                loading={datesLoading}
                value={calendarDate}
              />
            </LocalizationProvider>
          </Box>
        </Box>

      <Box
        sx={{
          border: "1px solid #EDEDED",
          borderRadius: "8px",
          display: "flex",
          flex: "1 1 50%",
          ml: "4px",
          p: "8px",
          flexDirection: "column",
          mt: "8px"
        }}
      >
        {bookingsAtMax
          ? <>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", p: "0 8px" }}>
                <WarningAmberIcon color="warning" />
                <Typography sx={(theme) => ({ fontSize: theme.typography.h3, pl: "8px" })}>Unable to book selected date</Typography>
              </Box>
              <Box sx={{ mt: "12px", p: "0 8px" }}>
                <Typography sx={(theme) => ({ fontSize: "14px", color: theme.palette.grey[500] })}>You are unable to book the selected date because you have reached your limit on bookings per week.</Typography>
              </Box>
            </>
          : <Typography sx={(theme) => ({ fontSize: theme.typography.h3 })}>Select Time</Typography>
        }
        {isTimesDataLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}><CircularProgress color="primary" size="3rem" thickness={4} sx={{ mt: '4rem', mb: '4rem' }} /></Box>
          : <>
              <Box sx={{ mt: "12px" }}>
                {times.morning.length > 0 ? <Typography sx={{ fontSize: "18px", pb: "8px" }}>Morning</Typography> : null}
                {times.morning.map((time: string) => {
                  return (
                    <Chip
                      label={time}
                      sx={(theme) => ({ backgroundColor: selectedBookingTime === time ? theme.palette.primary.light : "rgba(0, 109, 166, 0.11)", mr: "8px", mt: "8px" })}
                      onClick={() => timeSelected(time)}
                      key={time}
                    />
                  );
                })}
                {times.morning.length <= 0 && !bookingsAtMax ? <Typography sx={(theme) => ({ fontSize: "12px", textAlign: "center", color: "grey" })}>There are currently no morning time slots available</Typography> : null}
              </Box>
              <Box sx={{ mt: "22px" }}>
                {times.afternoon.length > 0 ? <Typography sx={{ fontSize: "18px", pb: "8px" }}>Afternoon</Typography> : null}
                {times.afternoon.map((time: string) => {
                  return (
                    <Chip
                      label={time}
                      sx={(theme) => ({ backgroundColor: selectedBookingTime === time ? theme.palette.primary.light : theme.palette.grey[200], mr: "8px", mt: "8px" })}
                      onClick={() => timeSelected(time)}
                      key={time}
                    />
                  );
                })}
                {times.afternoon.length <= 0 && !bookingsAtMax ? <Typography sx={(theme) => ({ fontSize: "12px", textAlign: "center", color: theme.palette.grey[500] })}>There are currently no afternoon time slots available</Typography> : null}
              </Box>
            </>
        }     
      </Box>
    </Box>)
    : null
  }

export default Calendar;
